import { useState } from "react";
import { Link } from "react-router-dom";
import { Boxes, Search, Download, Loader2, PackageOpen, ArrowLeft, Activity } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

export default function Library() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const lookup = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.purchases(email);
      setResult(r);
    } catch {
      toast.error("Could not load your purchases");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grain min-h-screen bg-slate-900 text-white">
      <div className="pointer-events-none absolute left-1/2 top-0 h-[360px] w-[600px] -translate-x-1/2 rounded-full bg-emerald-500/8 blur-[120px]" />
      <header className="relative border-b border-white/10">
        <div className="mx-auto flex max-w-[1000px] items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-2.5">
            <img src="/logo.png" alt="Ticker Templates" className="h-8 w-auto" />
          </Link>
          <Link to="/" className="flex items-center gap-2 text-sm text-slate-300 transition-colors duration-200 hover:text-white">
            <ArrowLeft className="h-4 w-4" /> Marketplace
          </Link>
        </div>
      </header>

      <main className="relative mx-auto max-w-[1000px] px-6 py-16">
        <span className="font-mono text-xs uppercase tracking-[0.2em] text-emerald-400">Your Downloads</span>
        <h1 className="mt-2 font-display text-4xl font-extrabold tracking-tighter sm:text-5xl">Purchase Library</h1>
        <p className="mt-3 max-w-lg text-base leading-relaxed text-slate-400">
          Enter the email you used at checkout to see every template you own and re-download it anytime.
        </p>

        <form onSubmit={lookup} className="mt-8 flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
            <input
              data-testid="library-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              className="w-full border border-white/15 bg-slate-950 py-3.5 pl-11 pr-4 text-white outline-none transition-colors duration-200 placeholder:text-slate-500 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500"
            />
          </div>
          <button
            data-testid="library-lookup-btn"
            disabled={loading}
            className="flex items-center justify-center gap-2 bg-emerald-500 px-6 py-3.5 font-semibold text-slate-900 transition-[background-color] duration-200 hover:bg-emerald-400 disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            Find my templates
          </button>
        </form>

        {result && (
          <div className="mt-12" data-testid="library-results">
            {result.count === 0 ? (
              <div className="flex flex-col items-center border border-white/10 bg-slate-950 py-20 text-center">
                <PackageOpen className="h-10 w-10 text-slate-600" />
                <p className="mt-4 text-slate-300">No purchases found for <span className="text-white">{result.email}</span>.</p>
                <p className="mt-1 text-sm text-slate-500">Make sure it matches the email you used at checkout.</p>
                <Link to="/" className="mt-6 bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-slate-900 transition-[background-color] duration-200 hover:bg-emerald-400">
                  Browse templates
                </Link>
              </div>
            ) : (
              <>
                <p className="mb-5 font-mono text-xs uppercase tracking-[0.15em] text-slate-500">
                  {result.count} template{result.count > 1 ? "s" : ""} owned
                </p>
                <div className="grid grid-cols-1 gap-4">
                  {result.purchases.map((p) => (
                    <div
                      key={p.session_id}
                      data-testid={`library-item-${p.product_id}`}
                      className="flex flex-col gap-4 border border-white/10 bg-slate-950 p-4 sm:flex-row sm:items-center"
                    >
                      <img src={p.image_url} alt="" className="h-16 w-24 flex-shrink-0 object-cover" />
                      <div className="min-w-0 flex-1">
                        <h3 className="font-display text-lg font-bold tracking-tight text-white">{p.title}</h3>
                        <p className="mt-0.5 font-mono text-xs uppercase tracking-wider text-slate-500">
                          {p.software_type} · {(p.formats || []).join(", ")} · ${p.amount}
                        </p>
                      </div>
                      <a
                        data-testid={`download-btn-${p.product_id}`}
                        href={api.downloadUrl(p.session_id)}
                        className="flex items-center justify-center gap-2 bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-slate-900 transition-[background-color] duration-200 hover:bg-emerald-400"
                      >
                        <Download className="h-4 w-4" /> Download
                      </a>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
